import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import type { DueDiligenceIntent, DueDiligenceIntentAnswers } from './dueDiligenceIntent'
import { toDueDiligenceIntentAnswers, toSourcingAnswers } from './dueDiligenceIntent'
import type { SourcingAnswers } from './sourcingRecommendation'

export function useDueDiligenceIntent(): {
  storedAnswers?: SourcingAnswers
  hasIntent: boolean
  isLoading: boolean
  saveAnswers: (answers: SourcingAnswers) => Promise<void>
} {
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  const { mutateAsync } = useUpdateResource<DueDiligenceIntent, DueDiligenceIntentAnswers>()

  const { data, isLoading } = useFetchResource<DueDiligenceIntent | null>({
    key: FetchKey.DueDiligenceIntent,
    endpoint: endpoints.dueDiligenceIntent,
    options: { staleTime: ONE_MINUTE_MS },
  })

  // Resolves either way: the notification is how a failure is handled, and callers fire this without awaiting.
  const saveAnswers = useCallback(
    (answers: SourcingAnswers): Promise<void> =>
      mutateAsync(
        { url: endpoints.dueDiligenceIntent, body: toDueDiligenceIntentAnswers(answers) },
        {
          onError: error => showErrorNotification({ requestError: error }),
          onSuccess: () => queryClient.invalidateQueries({ queryKey: [FetchKey.DueDiligenceIntent] }),
        },
      ).then(
        () => undefined,
        () => undefined,
      ),
    [mutateAsync, queryClient, showErrorNotification],
  )

  return {
    storedAnswers: data ? toSourcingAnswers(data.answers) : undefined,
    hasIntent: Boolean(data),
    isLoading,
    saveAnswers,
  }
}
